// Async Thunks
import {createAsyncThunk} from "@reduxjs/toolkit";
import * as tablesApi from "../../api/tables.api.ts"
import { UserRole } from '../../types'

const canManageTableDetails = (state: unknown): boolean => {
    const role = (state as { auth?: { user?: { role?: string } } }).auth?.user?.role
    return role === UserRole.SUPERADMIN || role === UserRole.ADMIN || role === UserRole.MARKER
}

export const fetchTables = createAsyncThunk(
    'tables/fetchTables',
    async (_, { rejectWithValue }) => {
        try {
            return await tablesApi.fetchTables()
        } catch (error: any) {
            return rejectWithValue(error.message)
        }
    }
)

export const fetchTableDetails = createAsyncThunk(
    'tables/fetchTableDetails',
    async (tableId: number, { rejectWithValue }) => {
        try {
            const [table, session] = await Promise.all([
                tablesApi.fetchTableDetails(tableId),
                tablesApi.fetchTableSession(tableId).catch(() => null)
            ])
            return { tableId, table, session }
        } catch (error: any) {
            return rejectWithValue(error.message)
        }
    }
)

export const pauseTable = createAsyncThunk(
    'tables/pauseTable',
    async (
        { tableId, mode = 'ACCRUED' }: { tableId: number; mode?: 'ACCRUED' | 'STAFF_MAX' },
        { dispatch, getState, rejectWithValue }
    ) => {
        try {
            await tablesApi.pauseTable(tableId, mode)
            // Regular users cannot read admin table details; their landing screen refreshes active sessions.
            if (canManageTableDetails(getState())) {
                await dispatch(fetchTableDetails(tableId))
            }
            return tableId
        } catch (error: any) {
            return rejectWithValue(error.message)
        }
    }
)

export const resumeTable = createAsyncThunk(
    'tables/resumeTable',
    async (tableId: number, { dispatch, getState, rejectWithValue }) => {
        try {
            await tablesApi.resumeTable(tableId)
            // Regular users cannot read admin table details; their landing screen refreshes active sessions.
            if (canManageTableDetails(getState())) {
                await dispatch(fetchTableDetails(tableId))
            }
            return tableId
        } catch (error: any) {
            return rejectWithValue(error.message)
        }
    }
)

export const startTable = createAsyncThunk(
    'tables/startTable',
    async (
        { tableId, durationMinutes, players, tariffId, pauseAfterDuration, withTrainer, videoRecordingEnabled }: {
            tableId: number
            durationMinutes: number
            players: Array<{ userId?: number; guestName?: string }>
            tariffId?: number | null
            pauseAfterDuration?: boolean
            withTrainer?: boolean
            videoRecordingEnabled?: boolean
        },
        { dispatch, rejectWithValue }
    ) => {
        try {
            await tablesApi.startTable(
                tableId,
                durationMinutes,
                players,
                tariffId,
                pauseAfterDuration,
                withTrainer,
                videoRecordingEnabled
            )
            await dispatch(fetchTables()) // Refresh all tables
            await dispatch(fetchTableDetails(tableId))
            return tableId
        } catch (error: any) {
            return rejectWithValue(error.message)
        }
    }
)

export const stopTable = createAsyncThunk(
    'tables/stopTable',
    async (
        { tableId, payerAssignmentIds, lateVideoRecording }: {
            tableId: number
            payerAssignmentIds?: number[]
            lateVideoRecording?: boolean
        },
        { dispatch, getState, rejectWithValue }
    ) => {
        try {
            await tablesApi.stopTable(tableId, payerAssignmentIds, lateVideoRecording)
            if (canManageTableDetails(getState())) {
                await dispatch(fetchTables())
            }
            return tableId
        } catch (error: any) {
            return rejectWithValue(error.message)
        }
    }
)

export const setVideoRecording = createAsyncThunk(
    'tables/setVideoRecording',
    async (
        { tableId, enabled }: { tableId: number; enabled: boolean },
        { dispatch, rejectWithValue }
    ) => {
        try {
            await tablesApi.setVideoRecording(tableId, enabled)
            await dispatch(fetchTableDetails(tableId))
            return { tableId, enabled }
        } catch (error: any) {
            return rejectWithValue(error.message)
        }
    }
)

export const addVideoCharge = createAsyncThunk(
    'tables/addVideoCharge',
    async (
        { tableId, chargeType }: { tableId: number; chargeType: 'FOUL_REVIEW' | 'CLIP_EDITING' },
        { rejectWithValue }
    ) => {
        try {
            const result = await tablesApi.addVideoCharge(tableId, chargeType)
            return { tableId, chargeType, ...result }
        } catch (error: any) {
            return rejectWithValue(error.message)
        }
    }
)